/**
 * InvoicePayments — list of Stripe Connect payments received for an invoice,
 * with a per-payment Refund button (workspace owner only).
 *
 * Shown inside InvoiceForm's edit view, ONLY when:
 *   - The invoice has been saved (has invoice_id)
 *   - At least one completed payment_transaction exists for it
 *
 * Refunds call POST /api/stripe-connect/refund/{payment_id} which routes
 * the refund through Stripe Connect on the subscriber's connected account.
 * Only workspace owners can refund; the backend enforces this.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, RotateCcw, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { apiFetch } from '../../fetchInterceptor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtUsd = (cents) => `$${((cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function InvoicePayments({ invoiceId, workspaceId }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refundFor, setRefundFor] = useState(null); // payment record being refunded
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refunding, setRefunding] = useState(false);

  const load = async () => {
    if (!invoiceId) return;
    try {
      const r = await apiFetch(
        `${API_URL}/api/invoices/${invoiceId}/payments?workspace_id=${workspaceId}`,
      );
      if (r.ok) {
        const data = await r.json();
        setPayments(Array.isArray(data) ? data : data.payments || []);
      }
    } catch (e) {
      console.error('Load payments failed', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  const openRefund = (p) => {
    setRefundFor(p);
    // Default: full refund of remaining (amount_cents - any prior refund)
    const refundable = ((p.amount_cents || 0) - (p.refund_amount_cents || 0)) / 100;
    setRefundAmount(refundable.toFixed(2));
    setRefundReason('');
  };

  const submitRefund = async () => {
    if (!refundFor) return;
    setRefunding(true);
    try {
      const payload = { reason: refundReason || undefined, notify_customer: true };
      const amount = parseFloat(refundAmount);
      const max = ((refundFor.amount_cents || 0) - (refundFor.refund_amount_cents || 0)) / 100;
      if (amount && amount < max) payload.amount = amount; // partial; omit for full
      const r = await apiFetch(
        `${API_URL}/api/stripe-connect/refund/${refundFor.transaction_id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        },
      );
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Refund failed');
      toast.success(data.message || `Refunded ${refundAmount}`);
      setRefundFor(null);
      load();
    } catch (e) {
      toast.error(e.message || 'Refund failed');
    } finally {
      setRefunding(false);
    }
  };

  if (loading) {
    return (
      <div className="text-xs text-muted-foreground flex items-center gap-2">
        <Loader2 className="w-3 h-3 animate-spin" /> Loading payments…
      </div>
    );
  }
  if (!payments.length) return null;

  return (
    <div className="space-y-3" data-testid="invoice-payments-section">
      <h3 className="text-sm font-semibold flex items-center gap-2">
        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
        Payments Received
        <Badge variant="outline" className="text-[10px]">{payments.length}</Badge>
      </h3>
      <div className="border border-zinc-200 dark:border-white/10 rounded-lg divide-y divide-zinc-100 dark:divide-white/5">
        {payments.map((p) => {
          const isRefunded = p.status === 'refunded' || p.refunded_at;
          const isPartial = p.status === 'partial_refund';
          const refundedCents = p.refund_amount_cents || 0;
          const remainingCents = (p.amount_cents || 0) - refundedCents;
          return (
            <div
              key={p.transaction_id}
              className="p-3 flex items-center justify-between gap-3 text-sm"
              data-testid={`payment-row-${p.transaction_id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium flex items-center gap-2 flex-wrap">
                  {fmtUsd(p.amount_cents)}
                  {p.surcharge_cents > 0 && (
                    <Badge variant="outline" className="text-[10px] font-normal">
                      incl. {fmtUsd(p.surcharge_cents)} card fee
                    </Badge>
                  )}
                  {p.platform_fee_cents > 0 && (
                    <Badge variant="outline" className="text-[10px] font-normal text-emerald-600 dark:text-emerald-400">
                      Platform fee: {fmtUsd(p.platform_fee_cents)}
                    </Badge>
                  )}
                  {isRefunded && (
                    <Badge className="text-[10px] bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">
                      Refunded {fmtUsd(refundedCents)}
                    </Badge>
                  )}
                  {isPartial && !isRefunded && (
                    <Badge className="text-[10px] bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                      Partial refund {fmtUsd(refundedCents)}
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {new Date(p.created_at).toLocaleString()} ·{' '}
                  {(p.payment_methods || ['card']).join(', ')}
                  {p.refund_reason && (
                    <span> · Reason: {p.refund_reason}</span>
                  )}
                </div>
              </div>
              {!isRefunded && remainingCents > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openRefund(p)}
                  data-testid={`refund-btn-${p.transaction_id}`}
                  className="shrink-0"
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                  Refund
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {/* Refund modal */}
      <Dialog open={!!refundFor} onOpenChange={(open) => !open && setRefundFor(null)}>
        <DialogContent data-testid="refund-modal">
          <DialogHeader>
            <DialogTitle>Issue Refund</DialogTitle>
            <DialogDescription>
              Refunds are processed by Stripe and typically appear on the customer's statement in
              5–10 business days. Only workspace owners can issue refunds.
            </DialogDescription>
          </DialogHeader>
          {refundFor && (
            <div className="space-y-4 py-2">
              <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 p-3 text-xs flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                <div>
                  Refunding {fmtUsd(refundFor.amount_cents)} originally charged on{' '}
                  {new Date(refundFor.created_at).toLocaleDateString()}.{' '}
                  Remember to post the offsetting journal entry manually in Journal Entries.
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="refund-amount">Amount to refund (USD)</Label>
                <Input
                  id="refund-amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={(((refundFor.amount_cents || 0) - (refundFor.refund_amount_cents || 0)) / 100).toFixed(2)}
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  data-testid="refund-amount-input"
                />
                <p className="text-[11px] text-muted-foreground">
                  Max refundable: {fmtUsd((refundFor.amount_cents || 0) - (refundFor.refund_amount_cents || 0))}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="refund-reason">Reason (optional, shared with customer)</Label>
                <Textarea
                  id="refund-reason"
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  placeholder="e.g. Service cancelled; goodwill credit; duplicate charge"
                  rows={3}
                  data-testid="refund-reason-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRefundFor(null)} disabled={refunding} data-testid="refund-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={submitRefund}
              disabled={refunding || !refundAmount || parseFloat(refundAmount) <= 0}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="refund-submit-btn"
            >
              {refunding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
              Issue Refund
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
